import { useEffect, useState } from 'react'
import { motion, useInView } from 'framer-motion'
import { Helmet } from "react-helmet";
import axios from 'axios';
import { Layout } from 'antd';
import { ArrowRight, CheckCircle, Rocket, Shield, Zap, TrendingUp, Code, Cloud, Database, Smartphone, Brain, Globe } from 'lucide-react';
import Loader from '../components/Loader';
import ImageLoader from '../components/ImageLoader';
import ProcessSection from '../components/ProcessSection';

import { fetchMediaMap } from '../utils/media';

// Animation variants
const fadeInUp = {
    hidden: { opacity: 0, y: 60, filter: 'blur(10px)' },
    visible: {
        opacity: 1,
        y: 0,
        filter: 'blur(0px)',
        transition: { type: 'spring', stiffness: 100, damping: 20 }
    }
}

const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: {
            staggerChildren: 0.1,
            delayChildren: 0.2
        }
    }
}

const Services = () => {
    const [loading, setLoading] = useState(true)
    const [servicesData, setServicesData] = useState([])
    const [skillsData, setSkillsData] = useState({})
    const [error, setError] = useState(null)

    useEffect(() => {
        fetchServicesData()
        window.scrollTo(0, 0);
    }, [])

    const fetchServicesData = async () => {
        try {
            const res = await axios.get('https://emr.metadei.in/wp-json/wp/v2/pages/15');
            const rawServices = res.data.acf?.services_box_home?.items_services_box_home || [];
            
            // Resolve numeric media IDs to full image URLs
            const imageIds = rawServices.map(item => item.image_services_home).filter(Boolean);
            const mediaMap = await fetchMediaMap(imageIds);

            const resolvedServices = rawServices.map(item => ({
                ...item,
                image_services_home: mediaMap[item.image_services_home] || (typeof item.image_services_home === 'string' ? item.image_services_home : '')
            }));

            setServicesData(resolvedServices);
            setSkillsData(res.data.acf?.skills_home || {});
            setLoading(false);
        } catch (e) {
            console.error('Error fetching services data:', e);
            setError(e);
            setLoading(false);
        }
    }

    if (loading) {
        return <Loader />;
    }

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background text-foreground">
                <div className="text-center max-w-md mx-auto px-4">
                    <div className="text-6xl mb-6">😕</div>
                    <h2 className="text-2xl font-bold font-heading mb-2">Unable to Load Services</h2>
                    <p className="text-muted-foreground mb-6">Please try again later.</p>
                    <button
                        onClick={() => window.location.href = '/'}
                        className="px-8 py-3 bg-primary text-primary-foreground rounded-full font-semibold hover:shadow-[0_0_20px_rgba(14,165,233,0.4)] transition-all"
                    >
                        Back to Home
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-background text-foreground min-h-screen overflow-x-hidden transition-colors duration-300">
            <Helmet>
                <title>Our Services - Connected Bytes Software Service</title>
                <meta name="description" content="Explore our comprehensive software development services including web development, mobile apps, cloud solutions, AI/ML, and digital transformation services." />
                <meta property="og:title" content="Our Services - Connected Bytes Software Service" />
                <meta property="og:description" content="Explore our comprehensive software development services tailored to your business needs." />
                <meta property="og:image" content="https://utfs.io/f/wzoc0SJJ8iMqmv8tcTUCV0hUWxjMFoDS38r1AKmZ5kftGwsN" />
                <meta property="og:url" content="https://connectedbytes.in/services" />
                <meta property="og:type" content="website" />
                <meta name="twitter:card" content="summary_large_image" />
            </Helmet>

            <Layout className='bg-transparent'>
                {/* Hero Section */}
                <div className="relative pt-28 sm:pt-32 pb-14 sm:pb-20 overflow-hidden border-b border-border">
                    {/* Immersive background pattern */}
                    <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:40px_40px] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)]"></div>
                    <div className="absolute top-0 right-0 w-1/3 h-1/2 bg-primary/20 blur-[120px] rounded-full pointer-events-none" />

                    <div className="max-w-7xl mx-auto px-4 text-center relative z-10">
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, ease: "easeOut" }}
                        >
                            <span className="inline-block px-3 py-1 mb-4 rounded-full bg-primary/10 border border-primary/20 text-primary font-semibold text-xs uppercase tracking-widest font-secondary">
                                What We Offer
                            </span>
                            <h1 className="text-3xl sm:text-5xl lg:text-7xl font-bold font-heading mt-3 sm:mt-4 mb-4">
                                Our Services
                            </h1>
                            <div className="w-20 sm:w-24 h-1 bg-gradient-to-r from-primary to-accent mx-auto rounded-full mb-5 sm:mb-6" />
                            <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto font-body px-2">
                                Comprehensive technology solutions designed to transform your business,
                                enhance efficiency, and drive relentless growth in the digital age.
                            </p>
                        </motion.div>
                    </div>
                </div>

                {/* Services Grid - Light & Vibrant for Dark Mode */}
                <div className="py-16 md:py-24 relative overflow-hidden">
                    {/* Brighter background gradients for dark mode */}
                    <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-accent/5 pointer-events-none" />

                    {/* Animated background elements - properly constrained within overflow-hidden */}
                    <div className="absolute top-0 left-1/4 w-72 sm:w-96 h-72 sm:h-96 bg-primary/15 rounded-full blur-[90px] sm:blur-[120px] animate-pulse pointer-events-none" />
                    <div className="absolute bottom-0 right-1/4 w-72 sm:w-96 h-72 sm:h-96 bg-accent/15 rounded-full blur-[90px] sm:blur-[120px] animate-pulse delay-1000 pointer-events-none" />
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[350px] sm:w-[600px] h-[350px] sm:h-[600px] bg-secondary/10 rounded-full blur-[100px] sm:blur-[150px] pointer-events-none" />

                    <div className="max-w-7xl mx-auto px-4 relative z-10">
                        <motion.div
                            variants={staggerContainer}
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true, amount: 0.05 }}
                            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8"
                        >
                            {servicesData.map((service, index) => (
                                <motion.div
                                    key={index}
                                    variants={fadeInUp}
                                    className="group relative bg-gradient-to-br from-white/5 via-white/5 to-white/3 backdrop-blur-md border border-white/15 rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 overflow-hidden"
                                >
                                    {/* Brighter animated gradient background on hover */}
                                    <div className="absolute inset-0 bg-gradient-to-br from-primary/0 via-accent/0 to-primary/0 group-hover:from-primary/20 group-hover:via-accent/15 group-hover:to-primary/20 transition-all duration-700" />

                                    {/* Enhanced shine effect */}
                                    <div className="absolute -inset-full top-0 left-0 w-full h-full bg-gradient-to-r from-transparent via-white/20 to-transparent -rotate-45 group-hover:translate-x-full transition-transform duration-1000" />

                                    {/* Brighter icon background glow */}
                                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-40 h-40 bg-primary/40 rounded-full blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                                    <div className="relative z-10">
                                        {/* Image Container - with brighter colors */}
                                        <div className="flex justify-center mb-6">
                                            <div className="relative">
                                                {/* Outer ring with brighter gradient */}
                                                <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-primary via-accent to-primary opacity-0 group-hover:opacity-100 blur-xl transition-opacity duration-500" />

                                                {/* Image wrapper with vibrant gradient */}
                                                <div className="relative w-28 h-28 rounded-2xl bg-gradient-to-br from-primary/40 via-primary/30 to-accent/40 flex items-center justify-center group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 shadow-xl group-hover:shadow-2xl border border-white/30 group-hover:border-white/50">
                                                    {/* Inner highlight */}
                                                    <div className="absolute inset-0 rounded-2xl bg-gradient-to-tr from-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                                                    <img
                                                        src={service.image_services_home}
                                                        alt={service.heading_services_home}
                                                        className="w-20 h-20 object-contain drop-shadow-xl group-hover:drop-shadow-2xl transition-all duration-300 brightness-110"
                                                        onError={(e) => {
                                                            e.target.onerror = null;
                                                            e.target.src = 'https://emr.metadei.in/wp-content/uploads/2024/12/enable_service.png';
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        {/* Title with vibrant gradient */}
                                        <h3 className="relative text-xl font-bold font-heading mb-3 text-center text-foreground group-hover:bg-gradient-to-r group-hover:from-primary group-hover:to-accent group-hover:bg-clip-text group-hover:text-transparent transition-all duration-300">
                                            {service.heading_services_home}
                                        </h3>

                                        {/* Brighter decorative line */}
                                        <div className="w-16 h-0.5 bg-gradient-to-r from-primary to-accent mx-auto mb-4 group-hover:w-28 transition-all duration-300 rounded-full" />

                                        {/* Description with better contrast */}
                                        <p className="relative text-muted-foreground text-sm leading-relaxed text-center group-hover:text-foreground transition-colors duration-300">
                                            {service.writeup_services_home}
                                        </p>


                                    </div>
                                </motion.div>
                            ))}
                        </motion.div>
                    </div>
                </div>

                {/* Why Choose Us Section */}
                <div className="py-24 bg-muted/30 border-y border-border relative overflow-hidden">
                    <div className="max-w-7xl mx-auto px-4 relative z-10">
                        <div className="text-center mb-16">
                            <h2 className="text-3xl md:text-5xl font-bold font-heading mb-6">Why Choose Us</h2>
                            <div className="w-24 h-1 bg-gradient-to-r from-primary to-accent mx-auto rounded-full" />
                        </div>
                        <motion.div
                            variants={staggerContainer}
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true, amount: 0.05 }}
                            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
                        >
                            {[
                                { icon: Rocket, title: "Innovation First", desc: "Cutting-edge solutions using the latest tech stacks." },
                                { icon: Shield, title: "Security Focused", desc: "Enterprise-grade security embedded by design." },
                                { icon: Zap, title: "Fast Delivery", desc: "Agile methodologies for rapid, reliable deployments." },
                                { icon: TrendingUp, title: "Scalable Solutions", desc: "Architectures built to scale effortlessly with you." }
                            ].map((feature, idx) => (
                                <motion.div
                                    key={idx}
                                    variants={fadeInUp}
                                    className="bg-card border border-border rounded-xl p-8 text-center shadow-sm hover:border-primary/50 transition-all group"
                                >
                                    <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6 group-hover:scale-110 group-hover:bg-primary transition-all duration-300">
                                        <feature.icon className="w-6 h-6 text-primary group-hover:text-primary-foreground transition-colors" />
                                    </div>
                                    <h3 className="text-lg font-bold font-heading mb-3">{feature.title}</h3>
                                    <p className="text-muted-foreground text-sm leading-relaxed">{feature.desc}</p>
                                </motion.div>
                            ))}
                        </motion.div>
                    </div>
                </div>

                {/* Process Section */}
                <div className="border-b border-border">
                    <ProcessSection />
                </div>

                {/* CTA Section */}
                <div className="py-16 md:py-24 relative overflow-hidden">
                    {/* Dynamic abstract background for CTA */}
                    <div className="absolute inset-0 bg-gradient-to-br from-primary via-primary/80 to-accent" />
                    <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10 mix-blend-overlay" />
                    <div className="absolute -top-40 -right-40 w-96 h-96 bg-white/20 blur-[100px] rounded-full pointer-events-none" />

                    <div className="relative z-10 max-w-4xl mx-auto text-center px-4">
                        <motion.h2
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5 }}
                            className="text-3xl sm:text-4xl md:text-5xl font-bold font-heading text-white mb-4 sm:mb-6"
                        >
                            Ready to Transform Your Business?
                        </motion.h2>
                        <motion.p
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.1 }}
                            className="text-base sm:text-xl text-white/90 mb-8 sm:mb-10 max-w-2xl mx-auto font-body"
                        >
                            Let's discuss how our elite engineering team can help you build the future.
                        </motion.p>
                        <motion.button
                            initial={{ opacity: 0, scale: 0.9 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            transition={{ duration: 0.3 }}
                            onClick={() => window.location.href = '/contact'}
                            className="group px-8 sm:px-10 py-3.5 sm:py-4 bg-white text-primary rounded-full font-bold shadow-[0_10px_30px_rgba(0,0,0,0.15)] hover:shadow-[0_15px_40px_rgba(0,0,0,0.25)] flex items-center justify-center mx-auto gap-3 text-base sm:text-lg"
                        >
                            Let's Talk <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                        </motion.button>
                    </div>
                </div>
            </Layout>
        </div>
    )
}

export default Services